import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, ShieldCheck, Building2, Paintbrush, Wrench, Layers, DollarSign, Ruler, Scissors, Scale } from "lucide-react";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import PageFAQ from "@/components/PageFAQ";
import RelatedServices from "@/components/RelatedServices";
import usePageSEO from "@/hooks/usePageSEO";
import { BUSINESS_LOGO } from "@/data/businessLogo";
import heroImg from "@/assets/wire-brushed-oak-dustless-sanding-refinishing-brighton-mi.webp";

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] as const },
};

const refinishingCostFAQs = [
  {
    q: "What is the average cost to refinish hardwood floors in Michigan?",
    a: "Refinishing cost depends on finish type, stain, molding work, furniture coordination and any custom color technique. Call (248) 325-8853 for a $50 estimate visit.",
  },
  {
    q: "Do you charge for estimates?",
    a: "Our estimate visit is $50. It is an expert diagnostic, not a free guess: we measure moisture, check the subfloor and remaining wear layer, and give you a written price. Call (248) 325-8853 or schedule a showroom visit at 33475 Seven Mile Rd, Livonia MI 48152.",
  },
  {
    q: "How long does hardwood floor refinishing take?",
    a: "Most projects take 2–3 days. Day one is sanding. Day two is stain application and first coat. Day three is final coats. Prefinished floors with heavy factory coats may require additional time.",
  },
  {
    q: "Is refinishing cheaper than replacing floors?",
    a: "Almost always yes. Replacement typically runs $8–15 per square foot installed. Refinishing restores your existing floors for a fraction of that cost — and on older solid hardwood, the refinished result is often superior to new flooring.",
  },
  {
    q: "What makes 1 Wood Floors different from other refinishers?",
    a: "Edmond Malaski holds Four NWFA Certifications as an Installer, Sand and Finish technician, Inspector, and Sales Advisor — one of very few contractors in Michigan to hold all four certifications simultaneously. As a Licensed Michigan Residential Builder we handle every aspect of the project including appliance coordination, plumbing disconnects, and trim work. We use the Pallmann Spider planetary sander for dustless refinishing and two-component waterborne finishes for superior durability.",
  },
];

const sections = [
  {
    icon: ShieldCheck,
    number: "01",
    title: "Are They Licensed and Insured?",
    content: "Before you even look at pricing, ask every contractor two questions: are you licensed, and are you insured? In Michigan, a contractor needs a license to legally enter into contracts above certain amounts. Insurance protects your home if something goes wrong — look for general liability, workers' compensation, and commercially covered vehicles. These things have a real cost, and contractors who skip them will quote you less. That lower price comes with real risk.",
  },
  {
    icon: Building2,
    number: "02",
    title: "Company Size and Reputation",
    content: "The size and reputation of a company affects its pricing. A company with in-house contractors, a customer service department, and an established reputation carries different overhead than a one-man operation using subcontractors. Neither is inherently wrong, but understand what you're buying. Reputation is built over time and it means something.",
  },
  {
    icon: Paintbrush,
    number: "03",
    title: "The Finish Makes a Huge Difference",
    content: "Most homeowners only think about water-based versus oil-based finish. But there are also hard wax oils, UV-cured hard wax oils, and two-component waterborne finishes — each with different costs, dry times, and durability profiles.",
    extra: "At 1 Wood Floors we use two-component waterborne finishes almost exclusively. Where a standard oil finish might cost $100–150 for five gallons, a single gallon of two-component waterborne finish can cost $150 or more. The better finish costs more upfront but provides superior protection, faster dry times, and greater abrasion resistance. We match the finish to the conversation — not every floor needs the same product.",
  },
  {
    icon: Wrench,
    number: "04",
    title: "Equipment and Dust Control",
    content: "Ask every contractor about their equipment. A Shop-Vac from Home Depot is not dust control. Our Pallmann Spider planetary sander combined with professional HEPA vacuum systems removes dust at the source. Good vacuums require serious CFM power and represent a real investment. The industry has come a long way from equipment with basic bags and no containment — but not every contractor has kept up.",
  },
  {
    icon: Layers,
    number: "05",
    title: "Stain Color",
    content: "If you want to stain your floors, budget an additional 50 cents to one dollar per square foot. We recommend narrowing to two or three colors before we start — applying multiple colors across multiple days adds cost and time. One thing homeowners often don't realize: the color on a sample board will look different once it's applied to your freshly sanded floor. Wood species, grain, and environmental conditions all affect how stain reads. We apply test patches directly on your floor to nail down the right color before committing to the full project.",
  },
  {
    icon: Scale,
    number: "06",
    title: "Full Service vs. Basic Service",
    content: "Are you getting a full-service refinisher or a basic one? At 1 Wood Floors, as a Licensed Michigan Residential Builder, we can disconnect and reconnect water lines, move appliances, coordinate with plumbers when valve replacements are needed, and handle all trim and molding work. Many contractors will leave your shoe molding in place and sand right up to it. The proper approach is to remove the shoe molding, sand fully to the wall, and install new molding after. That takes more time and costs more — but the result is a floor that looks finished, not rushed.",
  },
  {
    icon: Scissors,
    number: "07",
    title: "Prefinished Floors with Bevels",
    content: "If your floor is a factory prefinished floor with beveled edges — meaning you can feel the slight groove between boards — expect to pay nearly twice as much as a standard refinish. These floors have seven or more coats of factory urethane. We sand the bevel completely off, leaving substantial wood life remaining. Some contractors partially remove the bevel or skip this step entirely, leaving an uneven appearance. Ask any contractor you interview exactly how they handle prefinished beveled floors.",
  },
];

const RefinishingCost = () => {
  usePageSEO({
    title: "Cost to Refinish Hardwood Floors | 2026 Per Sq Ft Pricing",
    description: "What hardwood floor refinishing actually costs in 2026 — including dustless 98% containment, 2-component waterborne, and per-square-foot Michigan rates.",
    path: "/refinishing-cost",
  });

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>
        {/* ════════ HERO ════════ */}
        <section className="relative min-h-[55vh] md:min-h-[65vh] flex items-end overflow-hidden">
          <img
            width={1920} height={1080} loading="eager" decoding="async" fetchPriority="high"
            src={heroImg}
            alt="Close up of a wire brushed oak floor in a dark brown finish after refinishing, Brighton, MI"
            className="absolute inset-0 w-full h-full object-cover grayscale-[20%] contrast-[1.1]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/50 to-foreground/10" />
          <div className="relative z-10 max-w-[1200px] mx-auto px-5 md:px-12 pb-12 md:pb-20 w-full">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <PageBreadcrumb
                items={[
                  { label: "Services", to: "/refinishing" },
                  { label: "Refinishing Cost" },
                ]}
              />
              <h1 className="font-display text-[clamp(28px,5.5vw,56px)] font-semibold text-primary-foreground leading-[1.05] mb-3 mt-4">
                Hardwood Floor Refinishing Cost in Michigan
              </h1>
              <p className="text-[14px] md:text-[17px] font-light text-primary-foreground/70 max-w-2xl leading-relaxed">
                What every Michigan homeowner should know before hiring a refinisher — from an NWFA Certified Installer
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-7">
                <a
                  href="tel:2483258853"
                  className="bg-primary-foreground text-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-gold-400 transition-colors text-center inline-flex items-center justify-center gap-2"
                >
                  <Phone className="w-3.5 h-3.5" /> (248) 325-8853
                </a>
                <QuoteModal
                  trigger={
                    <button className="border border-primary-foreground/30 text-primary-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-primary-foreground/10 transition-colors text-center">
                      Book a $50 Estimate Visit
                    </button>
                  }
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* ════════ CONTENT SECTIONS 1-7 ════════ */}
        {sections.map((section, i) => {
          const Icon = section.icon;
          const isAlt = i % 2 === 1;
          return (
            <section key={section.number} className={`${isAlt ? "bg-muted/30" : "bg-background"} py-16 md:py-24 border-t border-border`}>
              <div className="max-w-[900px] mx-auto px-5 md:px-12">
                <motion.div {...fadeUp}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary text-lg font-bold">
                      {section.number}
                    </span>
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="font-display text-[clamp(22px,3.5vw,36px)] font-semibold text-foreground leading-[1.1] mb-6">
                    {section.title}
                  </h2>
                  <p className="text-[14px] md:text-[15px] font-normal text-muted-foreground leading-[1.8]">
                    {section.content}
                  </p>
                  {section.extra && (
                    <p className="text-[14px] md:text-[15px] font-normal text-muted-foreground leading-[1.8] mt-5">
                      {section.extra}
                    </p>
                  )}
                </motion.div>
              </div>
            </section>
          );
        })}

        {/* ════════ SECTION 8 — PRICING BREAKDOWN ════════ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[900px] mx-auto px-5 md:px-12">
            <motion.div {...fadeUp}>
              <div className="flex items-center gap-3 mb-4">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-gold-400/20 text-gold-400 text-lg font-bold">
                  08
                </span>
                <DollarSign className="w-5 h-5 text-gold-400" />
              </div>
              <h2 className="font-display text-[clamp(22px,3.5vw,36px)] font-semibold text-primary-foreground leading-[1.1] mb-6">
                How Pricing Actually Works
              </h2>
              <p className="text-[14px] md:text-[15px] font-normal text-primary-foreground/70 leading-[1.8] mb-8">
                Here is how the numbers build on a typical Michigan project. These are rough estimates — every floor is different and an in-home assessment is the only way to get an accurate number.
              </p>

              <div className="space-y-3">
                {[
                  { label: "Empty house, natural finish, oil-based, no molding", price: "~$4/sq ft" },
                  { label: "Add stain", price: "$5/sq ft" },
                  { label: "Add molding removal and replacement", price: "$6/sq ft" },
                  { label: "Upgrade to premium two-component waterborne finish", price: "$7.00–7.50/sq ft" },
                  { label: "Add furniture moving and full coordination", price: "$8.50–9.50/sq ft" },
                  { label: "Add custom coloring or complex color matching", price: "+$2–3/sq ft" },
                ].map((item) => (
                  <div key={item.label} className="flex items-start justify-between gap-4 border-b border-primary-foreground/10 pb-3">
                    <p className="text-[13px] md:text-[14px] font-normal text-primary-foreground/60 leading-relaxed">{item.label}</p>
                    <p className="text-[14px] md:text-[15px] font-semibold text-gold-400 whitespace-nowrap">{item.price}</p>
                  </div>
                ))}
              </div>

              <p className="text-[14px] md:text-[15px] font-normal text-primary-foreground/70 leading-[1.8] mt-8">
                Smaller projects also cost more per square foot. A 300 square foot job with a complex layout requires the same number of return trips as a 1,000 square foot job. The time to set up, return, and apply coats doesn't shrink proportionally with the square footage.
              </p>
              <p className="text-[14px] md:text-[15px] font-normal text-primary-foreground/70 leading-[1.8] mt-5">
                These are rough estimates only. Your actual cost depends on your floor's condition, species, layout complexity, and the services required. Call{" "}
                <a href="tel:2483258853" className="text-gold-400 font-semibold hover:text-primary-foreground transition-colors">(248) 325-8853</a>{" "}
                for a $50 estimate visit.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ════════ FAQ ════════ */}
        <PageFAQ suppressJsonLd
          faqs={refinishingCostFAQs}
          heading={<>Refinishing cost questions <em className="italic font-normal text-primary">answered.</em></>}
          subheading="Michigan Refinishing Cost FAQs"
        />

        {/* ════════ FINAL CTA ════════ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12 text-center">
            <motion.div {...fadeUp}>
              <h2 className="font-display text-[clamp(26px,4.5vw,48px)] font-semibold text-primary-foreground leading-[1.08] mb-4">
                Ready for an honest estimate
              </h2>
              <p className="text-[14px] md:text-[16px] font-normal text-primary-foreground/50 max-w-lg mx-auto leading-[1.7] mb-8">
                Call (248) 325-8853 or visit our Livonia showroom at 33475 Seven Mile Rd. $50 estimate visit. No pressure.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="tel:2483258853"
                  className="bg-primary-foreground text-foreground px-7 py-3.5 rounded-full text-sm font-medium hover:bg-gold-400 hover:text-foreground transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Phone className="w-3.5 h-3.5" /> Call (248) 325-8853
                </a>
                <QuoteModal
                  trigger={
                    <button className="border border-primary-foreground/30 text-primary-foreground px-7 py-3.5 rounded-full text-sm font-medium hover:bg-primary-foreground/10 transition-colors">
                      Schedule a Consultation
                    </button>
                  }
                />
              </div>
            </motion.div>
          </div>
        </section>

        <RelatedServices
          heading="More from our refinishing expertise."
          subheading="Related Services"
          links={[
            { title: "Dust Control Refinishing", desc: "See our full refinishing service page with equipment details, finish options, and process overview.", to: "/refinishing", tag: "Service" },
            { title: "Hardwood Installation", desc: "Starting fresh? Our NWFA Certified Installers specify and install wide-plank hardwood engineered for Michigan's climate.", to: "/installation", tag: "Service" },
            { title: "Subfloor Leveling & Repair", desc: "Before we sand, we evaluate. Uneven subfloors cause finish failures. We correct the foundation first.", to: "/subfloor-leveling", tag: "Service" },
            { title: "NWFA Certified Inspections", desc: "Floor failing after a refinish by another contractor? Our forensic inspections identify the root cause.", to: "/inspections", tag: "Service" },
          ]}
        />
      </main>
      <SiteFooter />

      {/* Service + FAQPage JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Hardwood Floor Refinishing",
            provider: {
              "@type": "LocalBusiness",
              logo: BUSINESS_LOGO,
              name: "1 Wood Floors",
              telephone: "+12483258853",
              address: {
                "@type": "PostalAddress",
                streetAddress: "33475 Seven Mile Rd",
                addressLocality: "Livonia",
                addressRegion: "MI",
                postalCode: "48152",
                addressCountry: "US",
              },
            },
            areaServed: { "@type": "State", name: "Michigan" },
            description: "Professional hardwood floor refinishing in Michigan by an NWFA Certified Installer. Dustless sanding, premium two-component waterborne finishes, and full-service coordination.",
          }),
        }}
      />
    </div>
  );
};

export default RefinishingCost;
